import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Helper function to parse date strings without timezone issues
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const [year, month, day] = dateString.split('T')[0].split('-');
  return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
};

const DownloadResearchModal = ({ isOpen, onClose, analyzedResearch, onDownload }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen) return null;

  const handleToggle = (researchId) => {
    setSelectedIds(prev =>
      prev.includes(researchId)
        ? prev.filter(id => id !== researchId)
        : [...prev, researchId]
    );
  };

  const handleSelectAll = () => {
    if (selectedIds.length === analyzedResearch.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(analyzedResearch.map(r => r.research_id));
    }
  };

  const handleDownload = async () => {
    if (selectedIds.length === 0) return;

    setDownloading(true);
    try {
      await onDownload(selectedIds);
      // Reset and close on success
      setSelectedIds([]);
      onClose();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  };

  const getDifficultyDisplay = (score) => {
    if (!score) return { label: 'N/A', color: 'bg-slate-100 text-slate-600' };

    let label = 'Simple';
    let color = 'bg-green-100 text-green-800';

    if (score > 3 && score <= 6) {
      label = 'Moderate';
      color = 'bg-yellow-100 text-yellow-800';
    } else if (score > 6 && score <= 8) {
      label = 'Complex';
      color = 'bg-orange-100 text-orange-800';
    } else if (score > 8) {
      label = 'Very Complex';
      color = 'bg-red-100 text-red-800';
    }

    return { label: `${score}/10 ${label}`, color };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">
              Download Research Report
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Select RFP research entries to include in the Word document
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FontAwesomeIcon icon="times" className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {analyzedResearch.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon="inbox" className="text-6xl text-slate-300 mb-4" />
              <p className="text-slate-600">No analyzed research entries available</p>
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="mb-4 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === analyzedResearch.length}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-slate-700">
                    Select All ({analyzedResearch.length} items)
                  </span>
                </label>
                <span className="text-sm text-slate-600">
                  {selectedIds.length} selected
                </span>
              </div>

              {/* Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider w-12">
                        Select
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        RFP Title
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Match Score
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Response Difficulty
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {analyzedResearch.map((research) => {
                      const difficulty = getDifficultyDisplay(research.response_difficulty_score);

                      return (
                        <tr
                          key={research.research_id}
                          className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                            selectedIds.includes(research.research_id) ? 'bg-primary-50' : ''
                          }`}
                          onClick={() => handleToggle(research.research_id)}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(research.research_id)}
                              onChange={() => handleToggle(research.research_id)}
                              className="w-4 h-4 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium text-slate-900">
                              {research.rfp_title}
                            </div>
                            {research.customer_name && (
                              <div className="text-xs text-slate-500">
                                {research.customer_name}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                            {research.due_date ? parseLocalDate(research.due_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            {research.recommendation_score ? (
                              <div className="flex items-center space-x-2">
                                <div className="flex-1 bg-slate-200 rounded-full h-2 max-w-[100px]">
                                  <div
                                    className={`h-2 rounded-full ${
                                      research.recommendation_score >= 80 ? 'bg-green-500' :
                                      research.recommendation_score >= 60 ? 'bg-amber-500' :
                                      'bg-red-500'
                                    }`}
                                    style={{ width: `${research.recommendation_score}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm font-medium text-slate-900">
                                  {research.recommendation_score}%
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">N/A</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${difficulty.color}`}>
                              {difficulty.label}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {selectedIds.length > 0 && (
              <span>
                <FontAwesomeIcon icon="info-circle" className="mr-2" />
                Report will be sorted by due date (soonest first)
              </span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={selectedIds.length === 0 || downloading}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {downloading ? (
                <>
                  <FontAwesomeIcon icon="spinner" className="fa-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="download" />
                  <span>Download Report ({selectedIds.length})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DownloadResearchModal;
